import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  adminDocuments,
  type AdminDocumentSummary,
  type AdminDocumentDetail,
  type DocReviewAction,
} from "@/lib/admin-endpoints";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 50;

const REVIEW_STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "pending",           label: "Pending" },
  { value: "reviewed_passed",   label: "Passed" },
  { value: "reviewed_blocked",  label: "Blocked" },
];

function ReviewStatusPill({ status }: { status?: string | null }) {
  if (!status) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500">
        pending
      </span>
    );
  }
  if (status === "reviewed_passed") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700">
        ✓ passed
      </span>
    );
  }
  if (status === "reviewed_blocked") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-50 text-rose-700">
        ✗ blocked
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500">
      {status}
    </span>
  );
}

export function AdminDocumentsPage() {
  const [reviewFilter, setReviewFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const [selectedDoc, setSelectedDoc] = useState<AdminDocumentDetail | null>(null);
  const [reviewAction, setReviewAction] = useState<DocReviewAction>("pass");
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "documents", reviewFilter, offset],
    queryFn: () =>
      adminDocuments.list({
        review_status: reviewFilter || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
  });

  const reviewMutation = useMutation({
    mutationFn: () =>
      adminDocuments.review(selectedDoc!.id, { action: reviewAction, notes: reviewNotes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "documents"] });
      setReviewOpen(false);
      setSelectedDoc(null);
      setReviewNotes("");
    },
  });

  const openReview = async (doc: AdminDocumentSummary) => {
    setDetailLoading(true);
    try {
      const detail = await adminDocuments.get(doc.id);
      setSelectedDoc(detail);
      setReviewAction("pass");
      setReviewNotes("");
      setReviewOpen(true);
    } finally {
      setDetailLoading(false);
    }
  };

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < total;

  const fmt = (d: string) =>
    new Date(d).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const fmtSize = (bytes?: number | null) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Documents</h1>
          <p className="text-xs text-slate-500 mt-0.5">Review uploaded supporting documents</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-1">
          {REVIEW_STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setReviewFilter(opt.value); setOffset(0); }}
              className={cn(
                "px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
                reviewFilter === opt.value
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-700",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-400">{total} total</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="space-y-0">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-14 border-b border-slate-100 last:border-0 animate-pulse bg-slate-50/50" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-slate-400 text-sm">
            No documents found
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3">File</th>
                <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3">Type</th>
                <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3">Uploader</th>
                <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3">Size</th>
                <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3">Status</th>
                <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3">Uploaded</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((doc) => (
                <tr
                  key={doc.id}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800 truncate max-w-[180px]">
                      {doc.file_name_original ?? <span className="text-slate-400 italic">unnamed</span>}
                    </div>
                    <div className="text-[10px] text-slate-400 font-mono mt-0.5 truncate max-w-[180px]">
                      {doc.id.slice(0, 8)}…
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {doc.document_type ?? <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    <div>{doc.uploader_email ?? "—"}</div>
                    <Link
                      to={`/admin/cases/${doc.case_id}`}
                      className="text-[10px] text-violet-500 hover:text-violet-700 font-mono"
                    >
                      case: {doc.case_id.slice(0, 8)}…
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtSize(doc.size_bytes)}</td>
                  <td className="px-4 py-3">
                    <ReviewStatusPill status={doc.review_status} />
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">{fmt(doc.created_at)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => openReview(doc)}
                      disabled={detailLoading}
                      className="text-[10px] font-semibold text-violet-600 hover:text-violet-800 disabled:opacity-50 transition-colors"
                    >
                      Review →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-slate-400">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              disabled={!hasPrev}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
            >
              ← Prev
            </button>
            <button
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              disabled={!hasNext}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Review modal */}
      {reviewOpen && selectedDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
            <h2 className="text-base font-bold text-slate-900 mb-1">Review document</h2>
            <p className="text-xs text-slate-500 mb-4">
              {selectedDoc.file_name_original ?? "Unnamed file"} · {fmtSize(selectedDoc.size_bytes)}
            </p>

            {/* File link */}
            <a
              href={selectedDoc.download_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-xl border border-slate-200 p-3 mb-5 hover:bg-slate-50 transition-colors group"
            >
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">
                  {selectedDoc.file_name_original ?? "View file"}
                </div>
                <div className="text-[10px] text-slate-400">
                  Expires {new Date(selectedDoc.download_url_expires).toLocaleTimeString()}
                  {selectedDoc.mime_type && ` · ${selectedDoc.mime_type}`}
                </div>
              </div>
              <svg className="w-4 h-4 text-slate-400 group-hover:text-violet-500 transition-colors shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>

            {/* Pass / Block selector */}
            <div className="flex gap-3 mb-5">
              <button
                onClick={() => setReviewAction("pass")}
                className={cn(
                  "flex-1 rounded-xl border-2 px-4 py-3 text-sm font-semibold transition-all",
                  reviewAction === "pass"
                    ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                    : "border-slate-200 text-slate-600 hover:border-slate-300",
                )}
              >
                ✓ Pass
              </button>
              <button
                onClick={() => setReviewAction("block")}
                className={cn(
                  "flex-1 rounded-xl border-2 px-4 py-3 text-sm font-semibold transition-all",
                  reviewAction === "block"
                    ? "border-rose-500 bg-rose-50 text-rose-800"
                    : "border-slate-200 text-slate-600 hover:border-slate-300",
                )}
              >
                ✗ Block
              </button>
            </div>

            <div className="mb-5">
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Notes <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                rows={3}
                placeholder="e.g. Passport scan is illegible, please re-upload"
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none transition-all"
              />
            </div>

            {reviewMutation.isError && (
              <p className="text-xs text-red-600 mb-4">Failed to save. Please try again.</p>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => { setReviewOpen(false); reviewMutation.reset(); }}
                className="flex-1 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 py-2.5 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => reviewMutation.mutate()}
                disabled={reviewMutation.isPending}
                className={cn(
                  "flex-1 rounded-xl text-white text-sm font-semibold py-2.5 transition-all disabled:opacity-60",
                  reviewAction === "pass"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-rose-600 hover:bg-rose-700",
                )}
              >
                {reviewMutation.isPending ? "Saving…" : reviewAction === "pass" ? "Pass document" : "Block document"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
